import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControlOptions, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService, patternValidator } from '@thehubz/client/core';
import { AuthApiService, ResetPasswordDto } from '@thehubz/client/data-access-api';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lh-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  form!: UntypedFormGroup;
  user!: string;
  logoUrl!: string;
  showSubmitButton = false;
  loading = false;
  validToken = false;
  message!: string;
  hide = true;
  visible = true;
  inputType = 'password';
  private token!: string;
  private subscription: Subscription = new Subscription();

  constructor(
    public formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthApiService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            patternValidator(/\d/, { hasNumber: true }),
            patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            patternValidator(/[a-z]/, { hasSmallCase: true }),
            patternValidator(/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/, { hasSpecialCharacters: true }),
          ],
        ],
        confirmPassword: ['', Validators.required],
      } as AbstractControlOptions,
      { validator: this.checkPasswords } as AbstractControlOptions,
    );
    this.getToken();
    this.logoUrl = 'assets/logos/brand-logo.svg';
  }

  getToken(): void {
    this.route.queryParamMap.subscribe((queryParams: any) => {
      this.token = queryParams.get('token');
    });
    if (this.token) {
      this.checkTokenValidation();
    }
  }

  checkTokenValidation() {
    this.loading = true;
    this.authService.authControllerCheckTokenValidation(this.token).subscribe({
      next: (res: { error?: string; message?: string }) => {
        if (res.message) {
          this.loading = false;
          this.validToken = true;
        } else if (res.error) {
          this.loading = false;
          this.validToken = false;
        }
      },
      error: (e: Error) => {
        this.loading = false;
        this.notification.addNotification({
          type: 'failure',
          message: e.message,
        });
      },
    });
  }

  checkPasswords(group: UntypedFormGroup) {
    const pass = group.controls['password'].value;
    const confirmPassword = group.controls['confirmPassword'].value;
    return pass === confirmPassword ? null : { notSame: true };
  }

  public onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      this.showSubmitButton = true;
      const resetPassObject: ResetPasswordDto = {
        token: this.token,
        password: this.form.value['password'],
      };
      this.subscription = this.authService.authControllerResetPassword(resetPassObject).subscribe({
        next: (res: { error?: string; message?: string }) => {
          this.loading = false;
          this.showSubmitButton = false;
          if (res.message) {
            this.notification.addNotification({
              type: 'success',
              message: res.message,
            });
            this.router.navigate(['login']);
          } else if (res.error) {
            this.notification.addNotification({
              type: 'failure',
              message: res.error,
            });
          }
        },
        error: (e: Error) => {
          this.loading = false;
          this.showSubmitButton = false;
          this.notification.addNotification({
            type: 'failure',
            message: e.message,
          });
        },
      });
    }
  }

  toggleVisibility() {
    this.hide = !this.hide;
    this.visible = !this.visible;
    if (this.visible) {
      this.inputType = 'password';
    } else {
      this.inputType = 'text';
    }
  }

  backToLogin() {
    this.router.navigate(['login']);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
